import React, { useState, useEffect } from 'react';
import { authHelpers } from '../global/supabase';
import '../global/styles.css';

function EmailConfirmation({ userEmail, onConfirmed, onGoToLogin }) {
  const [isConfirmed, setIsConfirmed] = useState(false);
  const [isChecking, setIsChecking] = useState(false);
  const [lastChecked, setLastChecked] = useState(null);

  // Check email confirmation status
  const checkConfirmationStatus = async () => {
    setIsChecking(true);
    try {
      // Force refresh session to get latest user data from Supabase
      const { data: refreshData } = await authHelpers.supabase.auth.refreshSession();

      if (refreshData?.user?.email_confirmed_at) {
        setIsConfirmed(true);
        return;
      }

      // Fallback: try getUser if refresh didn't work
      const { data } = await authHelpers.getCurrentUser();
      if (data?.user?.email_confirmed_at) {
        setIsConfirmed(true);
      }
    } catch (error) {
      console.log('Error checking confirmation:', error);
    }
    setIsChecking(false);
    setLastChecked(new Date());
  };

  // Auto-check every 5 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      if (!isConfirmed) {
        checkConfirmationStatus();
      }
    }, 5000);

    // Initial check
    checkConfirmationStatus();

    return () => clearInterval(interval);
  }, [isConfirmed]);

  const handleManualRefresh = () => {
    checkConfirmationStatus();
  };

  const formatTime = (date) => {
    return date ? date.toLocaleTimeString() : '';
  };

  return (
    <div className="container-center">
      <div className="card card-center card-large">
        <div className="logo-container">
          <div className={`status-icon ${isConfirmed ? 'success' : 'pending'}`}>
            {isConfirmed ? '✓' : '📧'}
          </div>
          <h2 className="title-auth">Check Your Email</h2>
        </div>

        <div className="content">
          <p className="subtitle">
            We've sent a confirmation link to:
          </p>
          <p className="email-display">{userEmail}</p>
          
          {!isConfirmed ? (
            <div>
              <p className="subtitle">Please check your email and click the confirmation link to activate your account.</p>
              <div className="status">
                <span className="status-dot pending"></span>
                <span>Waiting for confirmation...</span>
                {isChecking && <span className="spinner">⟳</span>}
              </div>
            </div>
          ) : (
            <div>
              <p className="text-success">Email confirmed successfully!</p>
              <div className="status">
                <span className="status-dot success"></span>
                <span>Account activated</span>
              </div>
            </div>
          )}

          {lastChecked && (
            <p className="text-small">
              Last checked: {formatTime(lastChecked)}
            </p>
          )}
        </div>

        <div className="form-group">
          {!isConfirmed ? (
            <button 
              onClick={handleManualRefresh}
              className="btn btn-secondary btn-full"
              disabled={isChecking}
            >
              {isChecking ? 'Checking...' : 'Refresh Status'}
            </button>
          ) : (
            <button 
              onClick={onGoToLogin}
              className="btn btn-primary btn-full"
            >
              Continue to Login
            </button>
          )}
        </div>

        <div className="help">
          <p>Didn't receive the email?</p>
          <ul>
            <li>Check your spam/junk folder</li>
            <li>Make sure you entered the correct email address</li>
            <li>Wait a few minutes - emails can take time to arrive</li>
          </ul>
        </div>
      </div>

      <footer className="footer">
        <p className="text-small">NarcoticTrack © 2025 NarcoticTrack Inventory System, LLC</p>
      </footer>
    </div>
  );
}

export default EmailConfirmation;